const VITE_PRELOAD_RELOAD_KEY = 'vite-preload-reload';
const VITE_PRELOAD_RELOAD_COOLDOWN_MS = 10_000;

/**
 * Returns the current Vite build mode (e.g., "development", "production", "test").
 *
 * @param env - The current environment.
 * @returns The current Vite mode string.
 */
export function getViteMode(env: ImportMetaEnv): string {
  return env.MODE;
}

/**
 * Retrieves PostHog-related environment configuration (Posthog's apiKey and apiHost) from Vite.
 *
 * @param env - The current environment.
 * @returns An object containing the PostHog API key and API host (if defined).
 */
export function getPosthogEnv(env: ImportMetaEnv): { apiKey?: string; apiHost?: string } {
  return {
    apiKey: env.VITE_PUBLIC_POSTHOG_KEY,
    apiHost: env.VITE_PUBLIC_POSTHOG_HOST,
  };
}

/**
 * Returns the current Sentry DSN from the environment.
 *
 * @param env - The current environment.
 * @returns The current Sentry DSN.
 */
export function getSentryDsn(env: ImportMetaEnv): string | undefined {
  return env.VITE_SENTRY_DSN;
}

/**
 * Reloads the current page.
 *
 * Extracted into a helper so page reloading can be injected or replaced during testing.
 */

export function reloadPage(): void {
  /* v8 ignore next -- @preserve */
  window.location.reload();
}

/**
 * Handles Vite preload errors by reloading the page to recover from stale or unavailable assets.
 *
 * Uses session storage to prevent repeated reloads within a short cooldown period if preload errors persist.
 * After the cooldown expires, a future preload error can trigger another recovery attempt.
 *
 * @param reload - Function used to reload the page.
 * @returns A cleanup function that removes the preload error listener.
 */
export function handleVitePreloadErrors(
  reload: () => void = reloadPage
): () => void {
  const handlePreloadError = () => {
    const previousReload = Number(
      sessionStorage.getItem(VITE_PRELOAD_RELOAD_KEY)
    );

    if (
      previousReload &&
      Date.now() - previousReload < VITE_PRELOAD_RELOAD_COOLDOWN_MS
    ) {
      return;
    }

    sessionStorage.setItem(VITE_PRELOAD_RELOAD_KEY, Date.now().toString());
    reload();
  };

  window.addEventListener('vite:preloadError', handlePreloadError);

  return () => {
    window.removeEventListener('vite:preloadError', handlePreloadError);
  };
}

/**
 * Determines whether PostHog analytics should be enabled.
 *
 * PostHog is enabled only when the app is running in production mode and both the API key and API host environment
 * variables are defined.
 *
 * @param mode - The current Vite mode (e.g., "development", "production").
 * @param posthogApiKey - The PostHog public API key from environment variables.
 * @param posthogApiHost - The PostHog API host URL from environment variables.
 * @returns True if PostHog should be initialized; otherwise false.
 */
export function shouldEnablePosthog(
  mode: string,
  posthogApiKey: string | undefined,
  posthogApiHost: string | undefined
): boolean {
  return mode === 'production' && !!posthogApiKey && !!posthogApiHost;
}

/**
 * Determines whether Sentry error monitoring should be initialized.
 *
 * Sentry is enabled only when (1) running in production mode and (2) a DSN is configured.
 *
 * @param mode - The current Vite mode (e.g., "development", "production", "test").
 * @param sentryDsn - The Sentry DSN from environment variables.
 * @returns True if Sentry should be initialized; otherwise false.
 */
export function shouldInitSentry(mode: string, sentryDsn: string | undefined): boolean {
  return mode === 'production' && !!sentryDsn;
}